import { Suspense, lazy, useCallback, useEffect, useState } from 'react';

import { api } from './api.js';
import { resetTelemetry } from './telemetry.js';
import { Header, Footer, RefPill } from './components/Chrome.jsx';
import Landing from './screens/Landing.jsx';
import Report from './report/Report.jsx';
import Admin, { INITIAL_ADMIN_UI } from './admin/Admin.jsx';

/*
 * survey-core is most of the bundle, against ~150KB for everything else.
 * Loaded eagerly it would be paid for by the landing page, by every staff
 * sign-in, and by a completed respondent who only ever opens their report --
 * none of whom render a survey.
 *
 * Split out, it is fetched when a respondent actually starts the assessment,
 * which is also the moment they are least sensitive to a short wait.
 */
const SurveyScreen = lazy(() => import('./survey/SurveyScreen.jsx'));

/**
 * The shell: who is signed in, which view is showing, and the body-level
 * state the stylesheet keys off.
 *
 * No router. The vanilla app switched views rather than URLs, and copying
 * that keeps behaviour identical and avoids a dependency for four views.
 * `/admin` stays what it was — an entry point that changes the sign-in copy,
 * NOT a way in. The same access code is required and the SERVER decides the
 * role; nothing here grants anything.
 *
 * `document.body` carries two things the CSS needs and React does not own:
 * `survey-locked`, which pins the survey's progress bar so it cannot scroll
 * away, and `data-view`, which lets the report bound its height without
 * bounding every other view.
 */
export default function App() {
  const [view, setView] = useState('login');
  const [session, setSession] = useState(null);
  const [typedCode, setTypedCode] = useState('');
  const [survey, setSurvey] = useState(null);
  const [report, setReport] = useState(null);
  const [fatal, setFatal] = useState(null);
  // The survey's line in the header: autosave state, or a failed submit.
  const [surveyStatus, setSurveyStatus] = useState(null);
  // Admin tab, owner filter, sort and DII filters. Held here, not in Admin,
  // so a drill-through into a company's report and back restores them, as
  // the vanilla admin module's state did.
  const [adminUi, setAdminUi] = useState(INITIAL_ADMIN_UI);

  const adminEntry = typeof window !== 'undefined'
    && window.location.pathname.replace(/\/+$/, '') === '/admin';

  // Mirrors the vanilla `show()`: body flags, then scroll to top. Without
  // the scroll reset, moving from a long survey page to the report lands
  // the reader halfway down it.
  useEffect(() => {
    document.body.classList.toggle('survey-locked', view === 'survey');
    document.body.dataset.view = `view-${view}`;
    window.scrollTo({ top: 0 });
    setSurveyStatus(null);
  }, [view]);

  const signOut = useCallback(() => {
    resetTelemetry();
    setSession(null);
    setSurvey(null);
    setReport(null);
    setTypedCode('');
    setAdminUi(INITIAL_ADMIN_UI);
    setView('login');
  }, []);

  /**
   * The server has resolved a code to a role. Route on what it said, never
   * on anything the client decided.
   */
  /**
   * Show a report, with its sector comparison if the respondent consented.
   *
   * The benchmark is fetched BEFORE the view switches, as the vanilla
   * showReport did, so the report renders once, complete, rather than
   * rendering bare and then growing comparisons. Declined consent or too
   * few peers means the comparison is absent, not an error.
   */
  const openReport = useCallback(async (payload) => {
    let benchmark = null;
    if (payload.consented) {
      try {
        benchmark = await api('/api/benchmark');
      } catch {
        benchmark = null;
      }
    }
    setReport({ report: payload, benchmark });
    setView('report');
  }, []);

  /**
   * Staff drill-through: open one company's report from an admin view.
   *
   * Navigation only. The server re-checks the staff session and the caller's
   * scope on this request; nothing here reaches a report that did not
   * already exist for them. Failure is reported with a blocking alert, as
   * the vanilla client did.
   */
  const openCompanyAsStaff = useCallback(async (company) => {
    try {
      const payload = await api(`/api/admin/report?companyId=${encodeURIComponent(company.companyId)}`);
      setReport({ report: payload.report, benchmark: payload.benchmark, staff: true });
      setView('report');
    } catch (error) {
      window.alert(error.status === 409
        ? 'That assessment is not complete yet, so it has no report.'
        : `Could not open that report: ${error.message}`);
    }
  }, []);

  const onSignedIn = useCallback(async (payload, code) => {
    setSession(payload);
    setTypedCode(code);

    if (payload.kind === 'owner') {
      setView('admin');
      return;
    }

    try {
      // A completed assessment goes straight to its report; anyone else goes
      // to the survey, resumed at the first unanswered required question.
      if (payload.hasResult) {
        await openReport(await api('/api/report'));
        return;
      }
      setSurvey(await api('/api/survey'));
      setView('survey');
    } catch (error) {
      setFatal(error.message);
    }
  }, [openReport]);

  const headerRight = (() => {
    if (view === 'admin' && session?.owner) {
      return <RefPill>{`${session.owner.name} · ${session.owner.role === 'admin' ? 'Admin' : 'Owner'}`}</RefPill>;
    }
    if (view === 'report' && report?.staff && session?.owner) {
      return <RefPill>{`${session.owner.name} · viewing a company`}</RefPill>;
    }
    if (view === 'report' && typedCode) return <RefPill>{typedCode}</RefPill>;
    if (view === 'survey' && surveyStatus) return <span className="saved-note">{surveyStatus}</span>;
    return null;
  })();

  return (
    <>
      <Header right={headerRight} />

      <main>
        {fatal && (
          <section className="view">
            <div className="landing-main">
              <div className="landing-col">
                <p className="login-error" role="alert">{fatal}</p>
                <button type="button" className="btn btn-primary" onClick={signOut}>Start again</button>
              </div>
            </div>
          </section>
        )}

        {!fatal && view === 'login' && <Landing onSignedIn={onSignedIn} adminEntry={adminEntry} />}

        {!fatal && view === 'survey' && survey && (
          <Suspense fallback={<section id="view-survey" className="view"><div className="survey-scroll"><div className="survey-container"><p className="map-loading">Loading your assessment…</p></div></div></section>}>
            <SurveyScreen
              definition={survey}
              answers={session?.answers ?? {}}
              onSubmitted={openReport}
              onStatus={setSurveyStatus}
            />
          </Suspense>
        )}

        {!fatal && view === 'report' && report && (
          <section id="view-report" className="view">
            <div className="report-main">
              <Report report={report.report} benchmark={report.benchmark}>
                {report.staff && (
                  <StaffReturnBar
                    companyName={report.report.company.companyName}
                    role={session?.owner?.role}
                    onBack={() => setView('admin')}
                  />
                )}
              </Report>
            </div>
          </section>
        )}

        {!fatal && view === 'admin' && (
          <Admin owner={session?.owner} ui={adminUi} setUi={setAdminUi} onOpenCompany={openCompanyAsStaff} />
        )}
      </main>

      <Footer />
    </>
  );
}

/** Lets staff get back to the cohort without signing in again. */
function StaffReturnBar({ companyName, role, onBack }) {
  return (
    <div className="staff-return">
      <button type="button" className="btn btn-tertiary" onClick={onBack}>← Back to companies</button>
      <span className="staff-return-note">{`Viewing ${companyName} as ${role === 'admin' ? 'an admin' : 'their owner'}`}</span>
    </div>
  );
}
